import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Settings2, X } from 'lucide-react';

/**
 * Status Color Legend Component
 * Displays a clickable legend of status colors that can filter the table
 */
export default function StatusColorLegend({
  statuses = [],
  statusColors = {},
  defaultColors = {},
  activeFilter,
  onFilterChange,
  onCustomizeClick,
  showCustomize = false,
  title
}) {
  const { t } = useTranslation();
  const legendTitle = title || t('dashboard.statusColorLegend', 'Status Color Legend');
  return (
    <Card className="bg-slate-900 border-slate-800 p-4" data-testid="status-legend">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-slate-400">{legendTitle}</h3>
        <div className="flex items-center gap-2">
          {activeFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onFilterChange('')}
              className="text-blue-400 hover:text-blue-300"
            >
              <X className="h-3 w-3 mr-1" />
              {t('dashboard.clearFilter', 'Clear filter')}: {activeFilter}
            </Button>
          )}
          {showCustomize && onCustomizeClick && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCustomizeClick}
              className="text-slate-400 hover:text-white"
              data-testid="customize-colors-btn"
            >
              <Settings2 className="h-4 w-4 mr-1" />
              {t('dashboard.customize', 'Customize')}
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {statuses.map(status => (
          <button
            key={status}
            onClick={() => onFilterChange(activeFilter === status ? '' : status)}
            className={`flex items-center gap-2 px-2 py-1 rounded transition-all hover:bg-slate-800 text-left ${
              activeFilter === status ? 'bg-slate-700 ring-1 ring-blue-500' : ''
            }`}
            title={`${t('dashboard.clickToFilter', 'Click to filter by')} ${status}`}
            data-testid={`legend-${status.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <span 
              className="w-4 h-3 rounded border border-slate-600 shrink-0"
              style={{ backgroundColor: statusColors[status] || defaultColors[status] }}
            ></span>
            <span className={`text-xs truncate ${activeFilter === status ? 'text-blue-400 font-medium' : 'text-slate-400'}`}>
              {t(`statuses.${status}`, status)}
            </span>
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-2">{t('dashboard.clickStatusToFilter', 'Click a status to filter shipments')}</p>
    </Card>
  );
}
